/**
 * Brotherhood+ client-side event tracker.
 * Batches lightweight anonymous behavioural events (page_view, click, rage_click,
 * time_on_page, scroll_depth) and flushes them to POST /api/events/track.
 *
 * PII-free by design — no message content, no journal text, no chat content is captured.
 *
 * SECURITY / DATA-CLASSIFICATION NOTE (code review 2026-02-14):
 *   `localStorage` keys used here (`bp_anon_id`, `bp_evt_queue`) hold ONLY
 *   an opaque random id + queued page-path/data-testid events. There is no
 *   auth token, no email, no journal content, and no chat content in
 *   localStorage — all sensitive credentials live in httpOnly cookies
 *   (bp_access / bp_refresh) which are not reachable from JS. This use of
 *   localStorage is intentional (survives full-page reload / offline retry)
 *   and is not an XSS-token-theft vector.
 */
import { http } from '@/lib/api';

const ANON_KEY = 'bp_anon_id';
const SESSION_KEY = 'bp_session_id';
const SESSION_START_KEY = 'bp_session_start';
const SESSION_LAST_KEY = 'bp_session_last';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function uid() {
  return 'x-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getAnonId() {
  let a = localStorage.getItem(ANON_KEY);
  if (!a) { a = uid(); localStorage.setItem(ANON_KEY, a); }
  return a;
}

function getSessionId() {
  const now = Date.now();
  const last = parseInt(sessionStorage.getItem(SESSION_LAST_KEY) || '0', 10);
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid || (last && now - last > SESSION_TIMEOUT_MS)) {
    sid = uid();
    sessionStorage.setItem(SESSION_KEY, sid);
    sessionStorage.setItem(SESSION_START_KEY, String(now));
  }
  sessionStorage.setItem(SESSION_LAST_KEY, String(now));
  return sid;
}

const QUEUE_KEY = 'bp_evt_queue';

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function saveQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-500))); }

let flushTimer = null;
function scheduleFlush(delay = 4000) {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, delay);
}

async function flush() {
  const q = loadQueue();
  if (q.length === 0) return;
  localStorage.setItem(QUEUE_KEY, '[]');
  try {
    await http.post('/events/track', { events: q });
  } catch {
    // On failure, requeue for the next flush attempt (bounded)
    const rescue = loadQueue();
    saveQueue([...q.slice(-100), ...rescue]);
  }
}

function pushEvent(ev) {
  const enriched = {
    ...ev,
    anonymous_id: getAnonId(),
    session_id: getSessionId(),
  };
  const q = loadQueue();
  q.push(enriched);
  saveQueue(q);
  scheduleFlush();
}

// -------- Click tracker (delegated) + Rage-click detection --------
let lastClicks = []; // [{el_id, t}]
function trackClickDelegate(e) {
  const target = e.target?.closest?.('[data-testid],button,a');
  if (!target) return;
  const el_id = target.getAttribute('data-testid') || target.tagName.toLowerCase();
  const now = Date.now();
  pushEvent({
    event_type: 'click',
    page: window.location.pathname,
    element_id: el_id,
  });
  // Rage: 3+ clicks on same element within 800ms
  lastClicks = lastClicks.filter((c) => now - c.t < 800 && c.el_id === el_id);
  lastClicks.push({ el_id, t: now });
  if (lastClicks.length >= 3) {
    pushEvent({
      event_type: 'rage_click',
      page: window.location.pathname,
      element_id: el_id,
    });
    lastClicks = [];
  }
}

// -------- Scroll depth (max per pageview) --------
let currentMaxScroll = 0;
function trackScroll() {
  const doc = document.documentElement;
  const pct = Math.round(
    ((window.scrollY + window.innerHeight) / Math.max(1, doc.scrollHeight)) * 100
  );
  if (pct > currentMaxScroll) currentMaxScroll = Math.min(100, pct);
}

// -------- Page view + time-on-page --------
let currentPath = window.location.pathname;
let currentPageStart = Date.now();

function flushCurrentPageTiming() {
  const dur = Date.now() - currentPageStart;
  if (dur > 400 && dur < 60 * 60 * 1000) {
    pushEvent({
      event_type: 'time_on_page',
      page: currentPath,
      duration_ms: dur,
    });
  }
  if (currentMaxScroll > 0) {
    pushEvent({
      event_type: 'scroll_depth',
      page: currentPath,
      value: currentMaxScroll,
    });
  }
  currentMaxScroll = 0;
}

function recordPageview(path) {
  pushEvent({ event_type: 'page_view', page: path });
  currentPath = path;
  currentPageStart = Date.now();
}

export function trackPageview(path) {
  if (path === currentPath) return;
  flushCurrentPageTiming();
  recordPageview(path);
}

export function trackEvent(payload) {
  pushEvent(payload);
}

// -------- Bootstrap --------
let bootstrapped = false;
export function initAnalytics() {
  if (bootstrapped) return;
  bootstrapped = true;
  recordPageview(window.location.pathname);
  document.addEventListener('click', trackClickDelegate, true);
  window.addEventListener('scroll', trackScroll, { passive: true });
  window.addEventListener('beforeunload', () => {
    flushCurrentPageTiming();
    // Use sendBeacon for last-ditch flush
    try {
      const q = loadQueue();
      if (q.length && navigator.sendBeacon) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/events/track`;
        const blob = new Blob([JSON.stringify({ events: q })], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        localStorage.setItem(QUEUE_KEY, '[]');
      }
    } catch { /* ignore */ }
  });
  // Periodic flush (every 30s)
  setInterval(flush, 30_000);
}
